import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { IsVerbose } from './Console';

let getAccessTokenFunc: (() => Promise<string>) | null = null;
let handleLogoutFunc: (() => void) | null = null;

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
// Queue of failed requests to retry after token refresh
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Configure the axios interceptor with auth functions
 * This should be called once during app initialization with the auth context functions
 * Returns a cleanup function to remove the interceptor
 */
export const setupAxiosInterceptor = (
  getAccessToken: () => Promise<string>,
  handleLogout: () => void
): (() => void) => {
  getAccessTokenFunc = getAccessToken;
  handleLogoutFunc = handleLogout;

  // Return cleanup function that clears the function references and resets state
  return () => {
    getAccessTokenFunc = null;
    handleLogoutFunc = null;
    // Reset module state to prevent stale isRefreshing blocking future requests
    if (isRefreshing) {
      processQueue(new AxiosError('Interceptor cleaned up'), null);
    }
    isRefreshing = false;
  };
};

/**
 * Create axios instance with 401 interceptor
 */
const axiosInstance: AxiosInstance = axios.create();

// Response interceptor to handle 401 and 504 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 504 (Gateway Timeout) - token expired, need to refresh and retry
    if (error.response?.status === 504 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!getAccessTokenFunc) {
        console.error('❌ Axios interceptor not configured with getAccessToken');
        return Promise.reject(error);
      }

      // If already refreshing, queue this request to avoid multiple simultaneous iframe requests
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            if (originalRequest.headers && newToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;
      IsVerbose() && console.log('🔄 Got 504 (token expired), attempting to refresh token...');

      try {
        const newToken = await getAccessTokenFunc();

        if (!newToken) {
          throw new Error('Failed to get new token');
        }

        IsVerbose() && console.log('✅ Token refreshed successfully after 504, retrying request');

        // Update the authorization header with new token
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        isRefreshing = false;

        // Retry the original request with new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('❌ Token refresh failed after 504');
        processQueue(error, null);
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }

    // Don't retry on other server errors (5xx) - these need to be handled by the caller
    if (error.response?.status && error.response.status >= 500) {
      console.error(`❌ Server error ${error.response.status}, not retrying`);
      return Promise.reject(error);
    }

    // Check if error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (!getAccessTokenFunc) {
        console.error('❌ Axios interceptor not configured with getAccessToken');
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            // Update the authorization header with the new token before retrying
            if (originalRequest.headers && newToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        IsVerbose() && console.log('🔄 Got 401, attempting to refresh token...');

        // Attempt to get a fresh token
        const newToken = await getAccessTokenFunc();

        if (!newToken) {
          throw new Error('Failed to get new token');
        }

        IsVerbose() && console.log('✅ Token refreshed successfully, retrying request');

        // Update the authorization header with new token
        if (originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        isRefreshing = false;

        // Retry the original request with new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('❌ Token refresh failed, redirecting to login');
        processQueue(error, null);
        isRefreshing = false;

        // Token refresh failed, logout and redirect to login
        if (handleLogoutFunc) {
          handleLogoutFunc();
        }

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
